import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Crosshair, Target, Star, Zap } from "lucide-react";
import { DiscordBadge } from "@/components/DiscordBadge";
const LEADERBOARDS = [
  { key: "kills", label: "Top Fraggers", color: "#F4D03F", icon: Crosshair, formatter: (p) => p.kills || 0 },
  { key: "kd", label: "Top K/D", color: "#00F0FF", icon: Target, formatter: (p) => (p.deaths > 0 ? (p.kills / p.deaths).toFixed(2) : (p.kills || 0).toFixed(2)) },
  { key: "mvps", label: "Top MVPs", color: "#FF007F", icon: Trophy, formatter: (p) => p.mvps || 0 },
  { key: "rating", label: "Top Rating", color: "#B084FF", icon: Star, formatter: (p) => (p.rating || 0).toFixed(2) },
  { key: "matches", label: "Most Matches", color: "#00FFB2", icon: Zap, formatter: (p) => p.matches || 0 },
];

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [top, setTop] = useState([]);
  const [prevMvps, setPrevMvps] = useState({});
  const [pulse, setPulse] = useState(null);

  const refresh = () => {
    api.get("/players").then((r) => {
      setPlayers((old) => {
        const snap = {};
        for (const p of old) snap[p.handle] = p.mvps || 0;
        setPrevMvps(snap);
        for (const np of r.data) {
          const before = snap[np.handle] ?? 0;
          if ((np.mvps || 0) > before) {
            setPulse(np.handle);
            setTimeout(() => setPulse((h) => (h === np.handle ? null : h)), 4000);
            break;
          }
        }
        return r.data;
      });
    }).catch(() => {});
    api.get("/player-standings").then((r) => setTop(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data));
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);
  const teamById = (id) => teams.find((t) => t.id === id);

  const mvpRace = useMemo(() => {
    return [...players]
      .filter((p) => (p.matches || 0) > 0)
      .sort((a, b) => (b.mvps || 0) - (a.mvps || 0))
      .slice(0, 5)
      .map((p) => ({ ...p, delta: (p.mvps || 0) - (prevMvps[p.handle] ?? p.mvps ?? 0) }));
  }, [players, prevMvps]);

  // Compute leaderboards from full players list — one per category
  const leaderboards = useMemo(() => {
    return LEADERBOARDS.map((lb) => {
      const sorted = [...players]
        .filter((p) => (p.matches || 0) > 0)
        .sort((a, b) => {
          if (lb.key === "kd") {
            const av = a.deaths > 0 ? a.kills / a.deaths : a.kills || 0;
            const bv = b.deaths > 0 ? b.kills / b.deaths : b.kills || 0;
            return bv - av;
          }
          return (b[lb.key] || 0) - (a[lb.key] || 0);
        })
        .slice(0, 10);
      return { ...lb, rows: sorted };
    });
  }, [players]);

  return (
    <div className="space-y-8" data-testid="players-root">
      <div>
        <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ ATHLETES</div>
        <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Players</h1>
      </div>

      {/* Iter25: Season MVP Race — live tick every 15s */}
      <section
        data-testid="mvp-race"
        className="border border-[#FF007F] bg-gradient-to-r from-[#FF007F]/10 via-transparent to-transparent p-5 sm:p-6"
      >
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] text-[#FF007F] uppercase flex items-center gap-2">
              <Trophy className="w-3.5 h-3.5" /> / SEASON MVP RACE
            </div>
            <div className="font-heading font-black uppercase tracking-tight text-xl mt-1">Who takes the crown?</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" /> Live · refreshes every 15s
          </div>
        </div>
        {mvpRace.length === 0 ? (
          <div className="text-center text-gray-500 text-xs font-mono uppercase tracking-widest py-6">
            No MVP votes yet this season.
          </div>
        ) : (
          <div className="grid gap-2">
            {mvpRace.map((p, idx) => {
              const t = teamById(p.team_id);
              const flashing = pulse === p.handle;
              return (
                <div
                  key={p.id}
                  data-testid={`mvp-race-${idx}`}
                  className={`flex items-center gap-3 border p-3 transition-colors ${
                    flashing ? "border-[#FF007F] bg-[#FF007F]/15 animate-pulse" : idx === 0 ? "border-[#F4D03F]/60 bg-[#0d0d16]" : "border-[#232330]"
                  }`}
                >
                  <div className="font-heading font-black text-xl w-8 text-center" style={{ color: idx === 0 ? "#F4D03F" : "#666" }}>
                    #{idx + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-black uppercase tracking-tight text-lg truncate flex items-center gap-2">
                      <Link to={p.id ? `/player-id/${encodeURIComponent(p.id)}` : `/player/${encodeURIComponent(p.handle)}`} className="hover:text-cyan-400 transition-colors">
                        {p.handle}
                      </Link>
                      {p.team_discord_linked && <DiscordBadge handle={p.handle} testid={`mvp-race-discord-${idx}`} />}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                      {(t?.tag || "FA")} · {p.role || "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-heading font-black text-3xl leading-none" style={{ color: t?.logo_color || "#FF007F" }}>
                      {p.mvps || 0}
                    </div>
                    {p.delta > 0 && (
                      <div data-testid={`mvp-race-delta-${idx}`} className="text-[10px] font-mono uppercase tracking-widest text-[#00FFB2] mt-1">
                        +{p.delta} ↑
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Tabs defaultValue="leaderboards" className="w-full">
        <TabsList className="bg-transparent border-b border-[#232330] rounded-none p-0 h-auto">
          <TabsTrigger
            value="leaderboards"
            data-testid="tab-leaderboards"
            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#F4D03F] data-[state=active]:bg-transparent data-[state=active]:text-[#F4D03F] uppercase tracking-widest text-xs font-bold px-4 py-3"
          >
            Season Leaderboards
          </TabsTrigger>
          <TabsTrigger
            value="roster"
            data-testid="tab-roster"
            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-cyan-400 data-[state=active]:bg-transparent data-[state=active]:text-cyan-400 uppercase tracking-widest text-xs font-bold px-4 py-3"
          >
            Roster
          </TabsTrigger>
          <TabsTrigger
            value="standings"
            data-testid="tab-player-standings"
            className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#FF007F] data-[state=active]:bg-transparent data-[state=active]:text-[#FF007F] uppercase tracking-widest text-xs font-bold px-4 py-3"
          >
            Full Rating table
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leaderboards" className="pt-6">
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="leaderboards-grid">
            {leaderboards.map((lb) => {
              const Icon = lb.icon;
              return (
                <div
                  key={lb.key}
                  data-testid={`leaderboard-${lb.key}`}
                  className="border border-[#232330] bg-[#101015]"
                  style={{ borderTop: `3px solid ${lb.color}` }}
                >
                  <div className="p-4 border-b border-[#232330] flex items-center gap-2">
                    <Icon className="w-4 h-4" style={{ color: lb.color }} />
                    <div className="font-heading font-black uppercase tracking-tight text-lg">{lb.label}</div>
                  </div>
                  {lb.rows.length === 0 ? (
                    <div className="p-6 text-center text-gray-500 text-xs font-mono uppercase tracking-widest">
                      No stats yet this season.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <tbody>
                        {lb.rows.map((p, idx) => {
                          const t = teamById(p.team_id);
                          return (
                            <tr key={p.id} className={`border-b border-[#232330] ${idx === 0 ? "bg-[#0d0d16]" : ""}`}>
                              <td className="px-3 py-2 font-mono text-gray-500 text-xs w-8">{String(idx + 1).padStart(2, "0")}</td>
                              <td className="px-3 py-2 font-heading font-bold uppercase tracking-tight">
                                <span className="inline-flex items-center gap-1.5">
                                  <Link to={p.id ? `/player-id/${encodeURIComponent(p.id)}` : `/player/${encodeURIComponent(p.handle)}`} className="hover:text-cyan-400 transition-colors">
                                    {p.handle}
                                  </Link>
                                  {p.team_discord_linked && <DiscordBadge handle={p.handle} size={10} testid={`lb-${lb.key}-discord-${idx}`} />}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: t?.logo_color || "#666" }}>{t?.tag || "FA"}</td>
                              <td className="px-3 py-2 font-mono font-bold text-right" style={{ color: lb.color }}>{lb.formatter(p)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="roster" className="pt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {players.map((p) => {
              const t = teamById(p.team_id);
              return (
                <div
                  key={p.id}
                  data-testid={`player-card-${p.handle}`}
                  className="border border-[#232330] bg-[#101015] p-5 hover:-translate-y-0.5 transition-transform"
                  style={{ borderTopColor: t?.logo_color, borderTopWidth: 2 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-mono text-[10px] tracking-widest text-gray-500">{p.country}</div>
                    <div className="font-mono text-[10px] tracking-widest text-cyan-400">{(p.role || "—").toUpperCase()}</div>
                  </div>
                  <div className="font-heading text-2xl font-black tracking-tight uppercase">{p.handle}</div>
                  <div className="text-xs text-gray-500 mt-1">{p.real_name}</div>
                  <div className="text-xs mt-3 font-mono text-gray-400" style={{ color: t?.logo_color }}>
                    {t?.name}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-6">
                    {[
                      { l: "K", v: p.kills || 0 }, { l: "D", v: p.deaths || 0 }, { l: "A", v: p.assists || 0 },
                      { l: "MP", v: p.matches || 0 }, { l: "MVP", v: p.mvps || 0 }, { l: "RTG", v: (p.rating ?? 0).toFixed(2) },
                    ].map((s) => (
                      <div key={s.l} className="border border-[#232330] p-2">
                        <div className="text-[9px] text-gray-500 font-mono tracking-widest">{s.l}</div>
                        <div className="font-mono font-bold text-sm text-white">{s.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="standings" className="pt-6">
          <div className="border border-[#232330] overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-[#232330] bg-[#0a0a12]">
                  {["#", "Handle", "Team", "Role", "MVPs", "Rating"].map((h) => (
                    <th key={h} className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 px-4 py-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top.map((p) => (
                  <tr key={p.id} className="border-b border-[#232330]" data-testid={`player-standing-${p.handle}`}>
                    <td className="px-4 py-3 font-mono text-gray-500">{String(p.rank).padStart(2, "0")}</td>
                    <td className="px-4 py-3 font-heading font-bold uppercase tracking-tight">
                      <span className="inline-flex items-center gap-1.5">
                        <Link to={p.id ? `/player-id/${encodeURIComponent(p.id)}` : `/player/${encodeURIComponent(p.handle)}`} className="hover:text-cyan-400 transition-colors" data-testid={`player-link-${p.handle}`}>
                          {p.handle}
                        </Link>
                        {p.team_discord_linked && <DiscordBadge handle={p.handle} testid={`player-standing-discord-${p.handle}`} />}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{teamById(p.team_id)?.tag || "FA"}</td>
                    <td className="px-4 py-3 text-cyan-400 font-mono text-xs">{p.role}</td>
                    <td className="px-4 py-3 font-mono">{p.mvps}</td>
                    <td className="px-4 py-3 font-mono font-bold text-[#FF007F]">{(p.rating ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
